import qs from 'qs'

import constants from 'config/constants'
import { mapRatingValueToBackgroundColor, mapRatingValueToEmoji } from 'domains/survey'
import { sortChoicesForDisplay } from 'libs/questions'

const LOGMEIN_RESCUE_INTEGRATION = 'logmeinrescue'
const LOGMEIN_SURVEY_BASE_URL = 'https://logmein.simplesat.io'
const SPROUT_SOCIAL_INTEGRATION = 'sproutsocial'
const SPROUT_SOCIAL_SURVEY_BASE_URL = 'https://sproutsocial.simplesat.io'

function isLogMeInRescueIntegration(currentSelectedIntegration) {
  return currentSelectedIntegration === LOGMEIN_RESCUE_INTEGRATION
}

function buildLogMeInRescueSurveyUrl(surveyToken) {
  return `${LOGMEIN_SURVEY_BASE_URL}/${surveyToken}`
}

function isSproutSocialIntegration(currentSelectedIntegration) {
  return currentSelectedIntegration === SPROUT_SOCIAL_INTEGRATION
}

function buildSproutSocialSurveyUrl(surveyToken) {
  return `${SPROUT_SOCIAL_SURVEY_BASE_URL}/${surveyToken}`
}

/**
 * @typedef {object} RatingIconDimension
 * @property {number} ratingIconSize
 * @property {number} padding
 * @property {number} labelFontSize
 *
 * @typedef {object} RatingIconDisplayOptions
 * @property {boolean} props.isRemovePlaceholder
 * @property {boolean} [props.isReverseOrder]
 * @property {boolean} [props.shouldHandleImagesNotLoaded]
 * @property {boolean} [props.shouldShowLabel]
 *
 * @typedef {object} Language
 * @property {string} props.short_choice_label_1
 * @property {string} props.short_choice_label_2
 * @property {string} props.short_choice_label_3
 * @property {string} props.short_choice_label_4
 * @property {string} props.short_choice_label_5
 *
 * @typedef CustomField
 * @property {object} [ticket]
 * @property {string} [teamMember]
 *
 * @typedef {object} SurveyConfig
 * @property {string} surveyToken
 * @property {object} primaryQuestion
 * @property {string} currentSelectedIntegration
 *
 * @param {SurveyConfig} surveyConfig
 * @param {object} placeholders
 * @param {'icon' | 'emoji' | 'basic' | 'colorful' | 'conservative'} embedStyleType
 * @param {RatingIconDisplayOptions} ratingIconDisplayOptions
 * @param {RatingIconDimension} [ratingIconDimension]
 * @param {CustomField} [customField]
 * @param {boolean} [isPreview]
 */
export const embedScript = (
  surveyConfig,
  placeholders,
  embedStyleType,
  ratingIconDisplayOptions,
  ratingIconDimension,
  customField,
  isPreview = false
) => {
  const { surveyToken, primaryQuestion, currentSelectedIntegration } = surveyConfig
  let script = `<h3 style="font-weight:bold;font-family:arial,sans-serif;margin:0;color:#373f46;">${primaryQuestion.text}</h3>`

  if (primaryQuestion.metric === 'CSAT') {
    // Canonical positive-first sort — raw builder order isn't stable, so we
    // normalise here to honor the PET-1967 canonical direction.
    const choices = sortChoicesForDisplay(primaryQuestion, ratingIconDisplayOptions.isReverseOrder)
    script += `<table cellpadding="${ratingIconDimension.padding}" style="border:0 !important;"><tr>`
    script += generateCSATChoice(
      surveyToken,
      placeholders,
      choices,
      currentSelectedIntegration,
      ratingIconDimension.ratingIconSize,
      embedStyleType,
      ratingIconDisplayOptions.shouldHandleImagesNotLoaded,
      ratingIconDisplayOptions.isRemovePlaceholder,
      ratingIconDisplayOptions.shouldShowLabel,
      ratingIconDimension.labelFontSize,
      customField
    )
  } else if (primaryQuestion.metric === 'CES') {
    script += `${generateCESEmbedScript(
      embedStyleType,
      primaryQuestion,
      surveyToken,
      placeholders,
      currentSelectedIntegration,
      ratingIconDisplayOptions.isRemovePlaceholder,
      customField,
      ratingIconDisplayOptions.isReverseOrder
    )}`
  } else if (primaryQuestion.metric === '5-STAR') {
    const choices = primaryQuestion.choices
    script += `<table cellpadding="${ratingIconDimension.padding}" style="border:0 !important;"><tr>`
    script += generate5StarEmbedScript(
      surveyToken,
      placeholders,
      choices,
      currentSelectedIntegration,
      ratingIconDimension.ratingIconSize,
      embedStyleType,
      ratingIconDisplayOptions.shouldHandleImagesNotLoaded,
      ratingIconDisplayOptions.isRemovePlaceholder,
      ratingIconDisplayOptions.shouldShowLabel,
      ratingIconDimension.labelFontSize,
      customField
    )
  } else {
    script += `${generateNPSEmbedScript(
      embedStyleType,
      primaryQuestion,
      surveyToken,
      placeholders,
      currentSelectedIntegration,
      ratingIconDisplayOptions.isRemovePlaceholder,
      customField
    )}`
  }
  if (!isPreview) {
    script += `${generateEmailOpenTrackingScript(
      surveyToken,
      placeholders,
      customField,
      currentSelectedIntegration
    )}`
  }

  script += `</tr></table>`
  return script
}

/**
 *
 * @param {*} placeholders
 * @param {CustomField} [customField={}]
 */
function createRatingParams(placeholders, customField = {}) {
  const basePlaceholders = createBasePlaceholders(placeholders)
  const ticketCustomFieldPlaceholders = createTicketCustomFieldPlaceholders(customField.ticket)
  const teamMemberCustomFieldPlaceholders = createTeamMemberCustomFieldPlaceholders(
    customField.teamMember
  )

  const ratingParams = {
    ...basePlaceholders,
    ...ticketCustomFieldPlaceholders,
    ...teamMemberCustomFieldPlaceholders,
  }
  const normalizedRatingParams = removeRatingParamKeyWithInvalidValue(ratingParams)
  return qs.stringify(normalizedRatingParams, { encode: false })
}

function createBasePlaceholders(placeholders) {
  return {
    customer_email: placeholders.customerEmail,
    customer_first_name: placeholders.customerFirstName,
    customer_last_name: placeholders.customerLastName,
    team_member_id: placeholders.teamMemberId,
    team_member_email: placeholders.teamMemberEmail,
    team_member_first_name: placeholders.teamMemberFirstName,
    team_member_last_name: placeholders.teamMemberLastName,
    company_name: placeholders.companyName,
    customer_id: placeholders.customerId,
    ticket_subject: placeholders.ticketSubject,
  }
}

function createTicketCustomFieldPlaceholders(ticketCustomFields) {
  if (ticketCustomFields) {
    return Object.entries(ticketCustomFields).reduce(
      (ticketCustomFieldPlaceholders, [ticketCustomField, ticketCustomFieldPlaceholder]) => {
        return {
          ...ticketCustomFieldPlaceholders,
          [`t_${ticketCustomField}`]: ticketCustomFieldPlaceholder,
        }
      },
      {}
    )
  } else {
    return {}
  }
}

function createTeamMemberCustomFieldPlaceholders(teamMemberCustomFields) {
  if (teamMemberCustomFields) {
    return Object.entries(teamMemberCustomFields).reduce(
      (
        teamMemberCustomFieldPlaceholders,
        [teamMemberCustomField, teamMemberCustomFieldPlaceholder]
      ) => {
        return {
          ...teamMemberCustomFieldPlaceholders,
          [`tm_${teamMemberCustomField}`]: teamMemberCustomFieldPlaceholder,
        }
      },
      {}
    )
  } else {
    return {}
  }
}

function removeRatingParamKeyWithInvalidValue(ratingParams) {
  return Object.entries(ratingParams).reduce(
    (ratingParams, [ratingParam, ratingParamPlaceholder]) => {
      const normalizedRatingParamPlaceholder =
        ratingParamPlaceholder && ratingParamPlaceholder.trim()
      if (normalizedRatingParamPlaceholder) {
        return {
          ...ratingParams,
          [ratingParam]: normalizedRatingParamPlaceholder,
        }
      } else {
        return ratingParams
      }
    },
    {}
  )
}

/**
 *
 * @param {*} rating
 * @param {*} surveyToken
 * @param {*} placeholders
 * @param {*} currentSelectedIntegration
 * @param {*} isRemovePlaceholder
 * @param {CustomField} [customField]
 */
export const generateSatisfactionRatingLink = (
  rating,
  surveyToken,
  placeholders,
  currentSelectedIntegration,
  isRemovePlaceholder,
  customField = {}
) => {
  let url = `${constants.backendHost}/api/embed/rating/${rating}/${surveyToken}/`

  if (placeholders.ticketId) {
    url += `${placeholders.ticketId.trim()}/`
  }

  url += `?source=${currentSelectedIntegration}`
  if (isRemovePlaceholder) {
    return url
  } else {
    const ratingParams = createRatingParams(placeholders, customField)
    if (ratingParams) {
      return url + '&' + ratingParams
    }

    return url
  }
}

/**
 *
 * @param {*} surveyToken
 * @param {*} placeholders
 * @param {*} currentSelectedIntegration
 * @param {*} isRemovePlaceholder
 * @param {CustomField} [customField]
 */
export const generateSatisfactionRatingLinkWithoutRating = (
  surveyToken,
  placeholders,
  currentSelectedIntegration,
  isRemovePlaceholder,
  customField = {}
) => {
  if (isLogMeInRescueIntegration(currentSelectedIntegration)) {
    return buildLogMeInRescueSurveyUrl(surveyToken)
  }

  if (isSproutSocialIntegration(currentSelectedIntegration)) {
    return buildSproutSocialSurveyUrl(surveyToken)
  }

  let url = `${constants.backendHost}/api/rating/${surveyToken}/`

  const trimmedTicketId = placeholders.ticketId.trim()
  if (trimmedTicketId !== '') {
    url += `${trimmedTicketId}/`
  }

  url += `?source=${currentSelectedIntegration}`
  if (isRemovePlaceholder) {
    return url
  } else {
    const ratingParams = createRatingParams(placeholders, customField)
    if (ratingParams) {
      return url + '&' + ratingParams
    }

    return url
  }
}

/**
 *
 * @param {string} imageURL
 * @param {boolean} shouldHandleImagesNotLoaded
 * @param {number} ratingIconSize
 */
export function getImageURL(imageURL, shouldHandleImagesNotLoaded, ratingIconSize) {
  if (shouldHandleImagesNotLoaded && canUseOpaqueImage(imageURL)) {
    return imageURL + `?background=white&size=${ratingIconSize}x${ratingIconSize}`
  }

  if (canUseLambdaImageProcessor(imageURL)) {
    return imageURL + `?size=${ratingIconSize}x${ratingIconSize}`
  }

  return imageURL
}

function canUseLambdaImageProcessor(imageURL) {
  return !/\.gif$/i.exec(imageURL)
}

export function canUseOpaqueImage(imageURL) {
  return Boolean(/\.png$/i.exec(imageURL))
}

/**
 *
 * @param {*} rating
 * @param {*} surveyToken
 * @param {*} placeholders
 * @param {*} choice
 * @param {*} currentSelectedIntegration
 * @param {*} ratingIconSize
 * @param {*} shouldHandleImagesNotLoaded
 * @param {*} isRemovePlaceholder
 * @param {*} shouldShowLabel
 * @param {*} labelFontSize
 * @param {CustomField} [customField]
 */
export const generateRatingImageLink = (
  rating,
  surveyToken,
  placeholders,
  choice = {},
  currentSelectedIntegration,
  ratingIconSize = 60,
  shouldHandleImagesNotLoaded = false,
  isRemovePlaceholder,
  shouldShowLabel,
  labelFontSize,
  customField = {}
) => {
  const choiceImage = getImageURL(choice.image, shouldHandleImagesNotLoaded, ratingIconSize)
  const satisfactionLink = generateSatisfactionRatingLink(
    rating,
    surveyToken,
    placeholders,
    currentSelectedIntegration,
    isRemovePlaceholder,
    customField
  )

  const imageLink = `<a href="${satisfactionLink}" target="_blank" rel="noopener"><img alt="${
    choice.label
  }" src="${choiceImage}" width="${ratingIconSize}" height="${ratingIconSize}" border="0" \
style="${_getRatingImageStyle(choice.value, shouldHandleImagesNotLoaded)}" /></a>`

  if (!shouldShowLabel) {
    return imageLink
  }

  return (
    imageLink +
    `<div style="margin-top:5px;">\
<a href="${satisfactionLink}" style="text-decoration:none;font-size:${labelFontSize}px;\
font-family:arial,sans-serif;color:#007EFF;" \
target="_blank" rel="noopener">${choice.label}</a></div>`
  )
}

export const generateEmailOpenTrackingScript = (
  surveyToken,
  placeholders,
  customFields,
  currentSelectedIntegration
) => {
  if (placeholders?.ticketId !== '') {
    return `<td style="display:none;"><img src="${getEmailOpenTrackingLink(
      surveyToken,
      placeholders,
      customFields,
      currentSelectedIntegration
    )}" alt="" width="1" height="1" border="0" style="height:1px!important;width:1px!important;border-width:0!important;margin-top:0!important;margin-bottom:0!important;margin-right:0!important;margin-left:0!important;padding-top:0!important;padding-bottom:0!important;padding-right:0!important;padding-left:0!important" /></td>`
  }
  return ''
}

export const getEmailOpenTrackingLink = (
  surveyToken,
  placeholders = {},
  customFields = {},
  currentSelectedIntegration = ''
) => {
  let url = `${constants.backendHost}/api/event/email-open-tracking/?`
  const basedParams = createRatingParams(placeholders, customFields)

  const trimmedTicketId = placeholders?.ticketId?.trim() || ''
  if (trimmedTicketId !== '') {
    url += `ticket_id=${trimmedTicketId}&`
  }
  url += `survey_token=${surveyToken}`

  if (currentSelectedIntegration) {
    url += `&source=${currentSelectedIntegration}`
  }

  if (basedParams) {
    url += `&${basedParams}`
  }

  return url
}

/**
 * @param {string} embedStyleType
 * @param {object} primaryQuestion
 * @param {string} surveyToken
 * @param {object} placeholders
 * @param {string} currentSelectedIntegration
 * @param {boolean} isRemovePlaceholder
 * @param {CustomField} customField
 */
export const generateNPSEmbedScript = (
  embedStyleType,
  primaryQuestion,
  surveyToken,
  placeholders,
  currentSelectedIntegration,
  isRemovePlaceholder,
  customField
) => {
  let script = ''
  const ratingLinkStyle = `text-decoration:none;display:block;font-family:arial,helvetica,sans-serif;font-size:18px;font-weight:bold;letter-spacing:0px;`

  if (embedStyleType === 'colorful') {
    script += `<table cellpadding="4" style="border:0 !important;padding-top:10px;display:inline-block;">`
    script += `<tr>`
    primaryQuestion.choices.forEach((choice) => {
      const bgColor = getBgColor(choice.sentiment)
      const fontColor = getFontColor(choice.sentiment)
      script += `<td style="background-color:${bgColor};border:none;border-radius:25px;width:32px;text-align:center;text-decoration:none;height:32px;margin-right:3px;">`
      script += `<a href="${generateSatisfactionRatingLink(
        choice.value,
        surveyToken,
        placeholders,
        currentSelectedIntegration,
        isRemovePlaceholder,
        customField
      )}" target="_blank" rel="noopener" style="${ratingLinkStyle}line-height:32px;color:${fontColor};">${
        choice.value
      }</a>`
      script += `</td>`
    })
  } else if (embedStyleType === 'conservative') {
    script += `<table cellpadding="4" style="border:0 !important;margin-top:10px;display:inline-block;border-collapse:collapse;">`
    script += `<tr>`
    primaryQuestion.choices.forEach((choice) => {
      script += `<td style="background-color:#ffffff;width:32px;text-align:center;text-decoration:none;height:32px;margin-right:3px;border:2px solid #373f46;">`
      script += `<a href="${generateSatisfactionRatingLink(
        choice.value,
        surveyToken,
        placeholders,
        currentSelectedIntegration,
        isRemovePlaceholder,
        customField
      )}" target="_blank" rel="noopener" style="${ratingLinkStyle}line-height:32px;color:#373f46;">${
        choice.value
      }</a>`
      script += `</td>`
    })
  } else {
    script += `<table cellpadding="4" style="border:0 !important;padding-top:10px;display:inline-block;">`
    script += `<tr>`
    primaryQuestion.choices.forEach((choice) => {
      script += `<td style="background-color:#EFEFEF;border:none;border-radius:9.2px;width:32px;text-align:center;text-decoration:none;height:40px;margin-right:3px;">`
      script += `<a href="${generateSatisfactionRatingLink(
        choice.value,
        surveyToken,
        placeholders,
        currentSelectedIntegration,
        isRemovePlaceholder,
        customField
      )}" target="_blank" rel="noopener" style="${ratingLinkStyle}line-height:40px;color:#373f46;">${
        choice.value
      }</a>`
      script += `</td>`
    })
  }

  return script
}

/**
 * @param {string} embedStyleType
 * @param {object} primaryQuestion
 * @param {string} surveyToken
 * @param {object} placeholders
 * @param {string} currentSelectedIntegration
 * @param {boolean} isRemovePlaceholder
 * @param {CustomField} customField
 */
export const generateCESEmbedScript = (
  embedStyleType,
  primaryQuestion,
  surveyToken,
  placeholders,
  currentSelectedIntegration,
  isRemovePlaceholder,
  customField,
  isReverseOrder = false
) => {
  let script = ''
  const ratingLinkStyle = `text-decoration:none;display:block;font-family:arial,helvetica,sans-serif;font-size:18px;font-weight:bold;letter-spacing:0px;`
  // Canonical positive-first sort (CES sorts by value DESC when no sentiment) —
  // isReverseOrder=true reverses to negative-first per PET-1967 flip semantics.
  const choices = sortChoicesForDisplay(primaryQuestion, isReverseOrder)

  if (embedStyleType === 'colorful') {
    script += `<table cellpadding="4" style="border:0 !important;padding-top:10px;display:inline-block;">`
    script += `<tr>`
    choices.forEach((choice) => {
      const bgColor = getBgColor(choice.sentiment)
      const fontColor = getFontColor(choice.sentiment)
      script += `<td style="background-color:${bgColor};border:none;border-radius:25px;width:32px;text-align:center;text-decoration:none;height:32px;margin-right:3px;">`
      script += `<a href="${generateSatisfactionRatingLink(
        choice.value,
        surveyToken,
        placeholders,
        currentSelectedIntegration,
        isRemovePlaceholder,
        customField
      )}" target="_blank" rel="noopener" style="${ratingLinkStyle}line-height:32px;color:${fontColor};">${
        choice.value
      }</a>`
      script += `</td>`
    })
  } else if (embedStyleType === 'conservative') {
    script += `<table cellpadding="4" style="border:0 !important;margin-top:10px;display:inline-block;border-collapse:collapse;">`
    script += `<tr>`
    choices.forEach((choice) => {
      script += `<td style="background-color:#ffffff;width:32px;text-align:center;text-decoration:none;height:32px;margin-right:3px;border:2px solid #373f46;">`
      script += `<a href="${generateSatisfactionRatingLink(
        choice.value,
        surveyToken,
        placeholders,
        currentSelectedIntegration,
        isRemovePlaceholder,
        customField
      )}" target="_blank" rel="noopener" style="${ratingLinkStyle}line-height:32px;color:#373f46;">${
        choice.value
      }</a>`
      script += `</td>`
    })
  } else {
    script += `<table cellpadding="4" style="border:0 !important;padding-top:10px;display:inline-block;">`
    script += `<tr>`
    choices.forEach((choice) => {
      script += `<td style="background-color:#EFEFEF;border:none;border-radius:9.2px;width:32px;text-align:center;text-decoration:none;height:40px;margin-right:3px;">`
      script += `<a href="${generateSatisfactionRatingLink(
        choice.value,
        surveyToken,
        placeholders,
        currentSelectedIntegration,
        isRemovePlaceholder,
        customField
      )}" target="_blank" rel="noopener" style="${ratingLinkStyle}line-height:40px;color:#373f46;">${
        choice.value
      }</a>`
      script += `</td>`
    })
  }

  return script
}

/**
 *
 * @param {string} surveyToken
 * @param {object} placeholders
 * @param {array} choices
 * @param {string} currentSelectedIntegration
 * @param {number} ratingIconSize
 * @param {string} iconType
 * @param {boolean} shouldHandleImagesNotLoaded
 * @param {boolean} isRemovePlaceholder
 * @param {boolean} shouldShowLabel
 * @param {number} labelFontSize
 * @param {CustomField} customField
 */
export function generate5StarEmbedScript(
  surveyToken,
  placeholders,
  choices,
  currentSelectedIntegration,
  ratingIconSize,
  iconType,
  shouldHandleImagesNotLoaded,
  isRemovePlaceholder,
  shouldShowLabel,
  labelFontSize,
  customField
) {
  let script = ''

  if (iconType === 'emoji') {
    const emojiChoice = '⭐️'
    choices.forEach((choice) => {
      script += `<td style="border:0;padding-left:0;padding-block:0;">${generateRatingEmojiLink(
        choice.value,
        surveyToken,
        placeholders,
        currentSelectedIntegration,
        ratingIconSize,
        isRemovePlaceholder,
        customField,
        emojiChoice
      )}</td>`
    })
  } else {
    choices.forEach((choice) => {
      script += `<td style="${_getTableDataStyle(shouldShowLabel)}">${generateRatingImageLink(
        choice.value,
        surveyToken,
        placeholders,
        choice,
        currentSelectedIntegration,
        ratingIconSize,
        shouldHandleImagesNotLoaded,
        isRemovePlaceholder,
        shouldShowLabel,
        labelFontSize,
        customField
      )}</td>`
    })
  }

  return script
}

/**
 *
 * @param {string} sentiment
 */
function getBgColor(sentiment) {
  if (sentiment === 'positive') {
    return '#D3F9DF'
  } else if (sentiment === 'neutral') {
    return '#FFEFD5'
  } else {
    return '#FFDDE1'
  }
}

/**
 *
 * @param {string} sentiment
 */
function getFontColor(sentiment) {
  if (sentiment === 'positive') {
    return '#4EC678'
  } else if (sentiment === 'neutral') {
    return '#F2A200'
  } else {
    return '#D00619'
  }
}

/**
 *
 * @param {string} surveyToken
 * @param {object} placeholders
 * @param {array} choices
 * @param {string} currentSelectedIntegration
 * @param {number} ratingIconSize
 * @param {string} iconType
 * @param {boolean} shouldHandleImagesNotLoaded
 * @param {boolean} isRemovePlaceholder
 * @param {boolean} shouldShowLabel
 * @param {number} labelFontSize
 * @param {CustomField} customField
 */
function generateCSATChoice(
  surveyToken,
  placeholders,
  choices,
  currentSelectedIntegration,
  ratingIconSize,
  iconType,
  shouldHandleImagesNotLoaded,
  isRemovePlaceholder,
  shouldShowLabel,
  labelFontSize,
  customField
) {
  let script = ''

  if (iconType === 'emoji') {
    choices.forEach((choice) => {
      const emojiChoice = mapRatingValueToEmoji(choice.value)
      script += `<td style="border:0;padding-left:0;padding-block:0;">${generateRatingEmojiLink(
        choice.value,
        surveyToken,
        placeholders,
        currentSelectedIntegration,
        ratingIconSize,
        isRemovePlaceholder,
        customField,
        emojiChoice
      )}</td>`
    })
  } else {
    choices.forEach((choice) => {
      script += `<td style="${_getTableDataStyle(shouldShowLabel)}">${generateRatingImageLink(
        choice.value,
        surveyToken,
        placeholders,
        choice,
        currentSelectedIntegration,
        ratingIconSize,
        shouldHandleImagesNotLoaded,
        isRemovePlaceholder,
        shouldShowLabel,
        labelFontSize,
        customField
      )}</td>`
    })
  }

  return script
}

/**
 *
 * @param {*} rating
 * @param {*} surveyToken
 * @param {*} placeholders
 * @param {*} currentSelectedIntegration
 * @param {*} ratingIconSize
 * @param {*} isRemovePlaceholder
 * @param {CustomField} customField
 * @param {string} emojiChoice
 */
function generateRatingEmojiLink(
  rating,
  surveyToken,
  placeholders,
  currentSelectedIntegration,
  ratingIconSize = 38,
  isRemovePlaceholder,
  customField,
  emojiChoice
) {
  return (
    `<a href="${generateSatisfactionRatingLink(
      rating,
      surveyToken,
      placeholders,
      currentSelectedIntegration,
      isRemovePlaceholder,
      customField
    )}" style="display:block;border:0;text-decoration:none;" target="_blank" rel="noopener">` +
    `<span style="font-size:${ratingIconSize}px">${emojiChoice}</span>` +
    `</a>`
  )
}

function _getRatingImageStyle(ratingValue, shouldHandleImagesNotLoaded) {
  const baseStyle = 'font-size:10px;font-weight:bold;color:#fff;display:block;margin:auto;'
  if (!shouldHandleImagesNotLoaded) {
    return baseStyle
  }

  const backgroundColor = mapRatingValueToBackgroundColor(ratingValue)
  return baseStyle + `background-color:${backgroundColor};`
}

function _getTableDataStyle(shouldShowLabel) {
  const style = 'vertical-align:top;text-align:center;'
  return shouldShowLabel ? style : `${style}padding-left:0;`
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/components/GenerateEmbedCodeModal/utils.js